import { useEffect, useRef, useState } from "react";
import { Loader2, TimerReset, Play, Flag, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ShareWinCard } from "./ShareWinCard";

const btn = "border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold";

export function PastPaper({ setId, title }) {
  const [phase, setPhase] = useState("idle"); // idle | generating | ready | running | marking | done
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [marks, setMarks] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const generate = async (refresh = false) => {
    setPhase("generating");
    try {
      const { data } = await api.post(`/study-sets/${setId}/past-paper${refresh ? "?refresh=true" : ""}`, null, { timeout: 90000 });
      setPaper(data);
      setAnswers({});
      setMarks({});
      setPhase("ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the exam.");
      setPhase("idle");
    }
  };

  const start = () => {
    setSecondsLeft((paper.time_minutes || 15) * 60);
    setPhase("running");
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          toast.warning("Time's up! Pens down — over to marking.");
          setPhase("marking");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const finish = () => {
    clearInterval(timerRef.current);
    setPhase("marking");
  };

  const questions = paper?.questions || [];
  const totalMarks = paper?.total_marks || questions.reduce((a, q) => a + (q.marks || 0), 0);
  const scoreFor = (q, i) => {
    if (q.type === "mcq") return answers[i] === q.answer ? q.marks : 0;
    return marks[i] ?? 0;
  };
  const totalScore = questions.reduce((a, q, i) => a + scoreFor(q, i), 0);
  const allMarked = questions.every((q, i) => q.type === "mcq" || marks[i] !== undefined);
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  if (phase === "idle" || phase === "generating")
    return (
      <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-center space-y-3" data-testid="pastpaper-idle">
        <TimerReset size={32} className="mx-auto" />
        <p className="font-heading text-xl font-semibold">Timed mini exam</p>
        <p className="text-sm text-gray-600">Mia writes a short ATAR-style paper on this topic — you sit it against the clock, then mark it like an examiner. The single best way to be exam-ready.</p>
        <button onClick={() => generate(false)} disabled={phase === "generating"} data-testid="pastpaper-generate-button"
          className={`${btn} bg-[#4D96FF] px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60`}>
          {phase === "generating" ? <><Loader2 size={18} className="animate-spin" /> Mia's writing your paper...</> : "Build my mini exam"}
        </button>
      </div>
    );

  if (phase === "ready")
    return (
      <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-center space-y-3" data-testid="pastpaper-ready">
        <p className="font-heading text-2xl font-semibold">{title}</p>
        <p className="text-sm font-bold">{questions.length} questions · {totalMarks} marks · {paper.time_minutes} minutes</p>
        <p className="text-sm text-gray-600">Phone away, one sitting, no peeking at notes. The timer starts when you hit the button.</p>
        <button onClick={start} data-testid="pastpaper-start-button" className={`${btn} bg-[#51CF66] px-8 py-3 inline-flex items-center gap-2`}>
          <Play size={18} /> Start the clock
        </button>
      </div>
    );

  if (phase === "running")
    return (
      <div className="space-y-4" data-testid="pastpaper-running">
        <div className={`sticky top-2 z-20 mx-auto w-fit px-6 py-2 border-2 border-black rounded-full font-heading text-2xl font-bold shadow-[4px_4px_0px_rgba(0,0,0,1)] ${secondsLeft <= 120 ? "bg-[#FF6B6B] text-white" : "bg-[#FFE066]"}`} data-testid="pastpaper-timer">
          {mm}:{ss}
        </div>
        {questions.map((q, i) => (
          <div key={i} className="bg-white border-2 border-black rounded-2xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-5 space-y-3">
            <p className="font-bold text-sm">Q{i + 1} <span className="text-gray-500">({q.marks} mark{q.marks === 1 ? "" : "s"})</span></p>
            <p className="text-sm font-medium">{q.question}</p>
            {q.type === "mcq" ? (
              <div className="space-y-2">
                {q.options.map((o, oi) => (
                  <button key={oi} onClick={() => setAnswers((prev) => ({ ...prev, [i]: oi }))} data-testid={`pp-q${i}-opt${oi}`}
                    className={`w-full text-left text-sm px-4 py-2.5 border-2 border-black rounded-xl font-medium transition-colors ${answers[i] === oi ? "bg-[#FFE066]" : "bg-white"}`}>
                    {o}
                  </button>
                ))}
              </div>
            ) : (
              <textarea rows={q.type === "extended" ? 6 : 3} value={answers[i] || ""} data-testid={`pp-q${i}-answer`}
                onChange={(e) => { const v = e.target.value; setAnswers((prev) => ({ ...prev, [i]: v })); }}
                placeholder="Write your answer..."
                className="w-full bg-white border-2 border-black rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50" />
            )}
          </div>
        ))}
        <button onClick={finish} data-testid="pastpaper-finish-button" className={`${btn} w-full bg-[#FF6B6B] text-white py-3 inline-flex items-center justify-center gap-2`}>
          <Flag size={18} /> Pens down — mark my paper
        </button>
      </div>
    );

  if (phase === "marking")
    return (
      <div className="space-y-4" data-testid="pastpaper-marking">
        <p className="text-sm font-bold text-center bg-[#F2C6DE] border-2 border-black rounded-xl px-4 py-2.5">Examiner mode: compare your answers to the model ones and award your marks honestly.</p>
        {questions.map((q, i) => (
          <div key={i} className="bg-white border-2 border-black rounded-2xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-5 space-y-2 text-sm">
            <p className="font-bold">Q{i + 1} ({q.marks} marks) — {q.question}</p>
            <p><span className="font-bold">You:</span> {q.type === "mcq" ? (answers[i] !== undefined ? q.options[answers[i]] : "no answer") : (answers[i] || "no answer")}</p>
            <div className="bg-[#FFE066]/50 border-2 border-black rounded-xl p-3">
              <p><span className="font-bold">Model answer:</span> {q.model_answer}</p>
              <p className="mt-1"><span className="font-bold">Marking guide:</span> {q.marking_guide}</p>
            </div>
            {q.type === "mcq" ? (
              <p className="font-bold" data-testid={`pp-q${i}-score`}>{answers[i] === q.answer ? `Correct — ${q.marks}/${q.marks}` : `Not this time — 0/${q.marks}`}</p>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold mr-1">Your marks:</span>
                {Array.from({ length: q.marks + 1 }, (_, m) => (
                  <button key={m} onClick={() => setMarks((prev) => ({ ...prev, [i]: m }))} data-testid={`pp-q${i}-mark${m}`}
                    className={`w-8 h-8 border-2 border-black rounded-full text-xs font-bold ${marks[i] === m ? "bg-[#51CF66]" : "bg-white"}`}>
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <button onClick={() => setPhase("done")} disabled={!allMarked} data-testid="pastpaper-see-score-button"
          className={`${btn} w-full bg-[#4D96FF] py-3 disabled:opacity-50`}>
          {allMarked ? "See my result" : "Award marks for every written answer first"}
        </button>
      </div>
    );

  const pct = totalMarks ? Math.round((totalScore / totalMarks) * 100) : 0;
  return (
    <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-center space-y-4" data-testid="pastpaper-result">
      <p className="font-heading text-5xl font-bold">{totalScore}/{totalMarks}</p>
      <p className="font-bold">{pct >= 80 ? "A-band performance — you're exam ready!" : pct >= 60 ? "Solid — one more pass at the weak spots and you're there." : "Great rehearsal — now you know exactly what to revise."}</p>
      <div className="flex flex-col items-center gap-3">
        <ShareWinCard stat={`${totalScore}/${totalMarks}`} title="Mini exam done!" sub={`Timed paper: ${title}`}
          color={pct >= 80 ? "#FFE066" : "#4D96FF"} testId="share-pastpaper-button" />
        <button onClick={() => generate(true)} data-testid="pastpaper-retry-button" className={`${btn} bg-white px-6 py-2.5 inline-flex items-center gap-2 text-sm`}>
          <RotateCw size={15} /> New paper, same topic
        </button>
      </div>
    </div>
  );
}
